// src/pages/LogsPage.js
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  MenuItem,
  Stack,
  InputLabel,
  FormControl,
  Select,
  Button,
  Chip
} from '@mui/material';
import DynamicDataGrid from '../components/DynamicDataGrid';
import { motion } from 'framer-motion';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { fetchLogs } from '../slices/logsSlice';
import { fetchAccountUsers } from '../slices/accountSlice'; // Add this import
import HistoryIcon from '@mui/icons-material/History';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

const MotionPaper = motion(Paper);

// Shared Cairo-styled control sx for filter inputs
const controlSx = {
  fontFamily: 'Cairo, Tajawal, sans-serif',
  '& .MuiOutlinedInput-root': {
    borderRadius: 2.5,
    bgcolor: '#F8F9FF',
    fontFamily: 'Cairo, Tajawal, sans-serif',
    '& fieldset': { borderColor: '#E5E7EB' },
    '&:hover fieldset': { borderColor: '#4338CA' },
    '&.Mui-focused fieldset': { borderColor: '#4338CA' },
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'Cairo, Tajawal, sans-serif',
    '&.Mui-focused': { color: '#4338CA' },
  },
};

const LogsPage = () => {
  const [searchNote, setSearchNote] = useState('');
  const [searchNoteInput, setSearchNoteInput] = useState('');
  const dispatch = useDispatch();
  const [selectedUser, setSelectedUser] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

  // ✅ Get users from Redux store instead of dummy data
  const { list: logs, count, status } = useSelector((state) => state.logs);
  const { users } = useSelector((state) => state.account);

  useEffect(() => {
    dispatch(fetchAccountUsers()); // ✅ Fetch real users
  }, [dispatch]);

  // Debounce the free-text note search so we don't hit the API on every keystroke
  useEffect(() => {
    const handle = setTimeout(() => setSearchNote(searchNoteInput), 400);
    return () => clearTimeout(handle);
  }, [searchNoteInput]);

  // Reset to first page whenever a filter changes, without triggering an extra fetch
  useEffect(() => {
    setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchNote, selectedUser, startDate, endDate]);

  useEffect(() => {
    dispatch(fetchLogs({
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
      note: searchNote,
      user: selectedUser,
      startDate,
      endDate,
    }));
  }, [dispatch, paginationModel, searchNote, selectedUser, startDate, endDate]);

  const handleResetFilters = () => {
    setSearchNoteInput('');
    setSearchNote('');
    setSelectedUser('');
    setStartDate('');
    setEndDate('');
  };

  const columns = [
    {
      field: 'timestamp',
      headerName: 'التاريخ والوقت',
      flex: 1,
      renderCell: (params) => {
        // Format the timestamp to DD-MM-YYYY HH:mm:ss
        return dayjs(params.value).format('DD-MM-YYYY HH:mm:ss');
      }
    },
    {
      field: 'user',
      headerName: 'المستخدم',
      flex: 1,
      renderCell: (params) => {
        // ✅ Find user display name from users array
        const user = users.find(u => u.username === params.value || u.id === params.value);
        return user ? user.display_name : params.value;
      }
    },
    {
      field: 'note',
      headerName: 'الوصف',
      flex: 2
    }
  ];

  // Active filter chips derived from current filter state (display only)
  const activeFilters = [];
  if (searchNoteInput) activeFilters.push({ key: 'note', label: `الوصف: ${searchNoteInput}` });
  if (selectedUser) {
    const u = users.find((x) => x.username === selectedUser || x.id === selectedUser);
    activeFilters.push({ key: 'user', label: `المستخدم: ${u ? u.display_name : selectedUser}` });
  }
  if (startDate) activeFilters.push({ key: 'start', label: `من: ${startDate}` });
  if (endDate) activeFilters.push({ key: 'end', label: `إلى: ${endDate}` });

  return (
    // 🎨 Design System Applied
    <Box
      sx={{
        direction: 'rtl',
        fontFamily: 'Cairo, Tajawal, sans-serif',
        p: { xs: 2, md: 3 },
        bgcolor: '#F8F9FF',
        minHeight: '100vh',
      }}
    >
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {/* Header Section */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                borderRadius: 2.5,
                width: 52,
                height: 52,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <HistoryIcon sx={{ color: '#fff', fontSize: 30 }} />
            </Box>
            <Box>
              <Typography
                variant="h5"
                fontWeight={800}
                color="#fff"
                sx={{ fontFamily: 'Cairo, Tajawal, sans-serif' }}
              >
                سجل التغييرات
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255,255,255,0.8)',
                  mt: 0.25,
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                }}
              >
                تتبّع ومراجعة سجل النشاطات والتغييرات في النظام
              </Typography>
            </Box>
          </Stack>
        </MotionPaper>

        {/* Filters Card */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
          sx={{
            borderRadius: 3,
            p: { xs: 2, sm: 2.5 },
            mb: 3,
            bgcolor: '#fff',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
            border: '1px solid #E5E7EB',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              color: '#1E1B4B',
              mb: 2,
              fontFamily: 'Cairo, Tajawal, sans-serif',
            }}
          >
            تصفية السجلات
          </Typography>

          <Stack useFlexGap direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-end">
            <TextField
              label="بحث في الوصف"
              variant="outlined"
              fullWidth
              value={searchNoteInput}
              onChange={(e) => setSearchNoteInput(e.target.value)}
              sx={controlSx}
            />
            <FormControl fullWidth sx={controlSx}>
              <InputLabel id="user-filter-label">المستخدم</InputLabel>
              <Select
                labelId="user-filter-label"
                value={selectedUser}
                label="المستخدم"
                onChange={(e) => setSelectedUser(e.target.value)}
              >
                <MenuItem value="">الكل</MenuItem>
                {/* ✅ Use real users from Redux store */}
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.username}>
                    {user.display_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="من تاريخ"
              type="date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              sx={controlSx}
            />
            <TextField
              label="إلى تاريخ"
              type="date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              sx={controlSx}
            />
            <Button
              variant="outlined"
              startIcon={<RestartAltIcon />}
              onClick={handleResetFilters}
              sx={{
                minWidth: 130,
                height: 56,
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                fontFamily: 'Cairo, Tajawal, sans-serif',
                borderColor: '#4338CA',
                color: '#4338CA',
                '&:hover': { borderColor: '#3730A3', bgcolor: '#EEF2FF' },
              }}
            >
              إعادة تعيين
            </Button>
          </Stack>

          {/* Active filter chips bar */}
          {activeFilters.length > 0 && (
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                borderRadius: 2.5,
                bgcolor: '#EEF2FF',
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: '#4338CA',
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                }}
              >
                الفلاتر النشطة:
              </Typography>
              {activeFilters.map((f) => (
                <Chip
                  key={f.key}
                  label={f.label}
                  size="small"
                  sx={{
                    bgcolor: '#fff',
                    color: '#4338CA',
                    fontWeight: 700,
                    fontSize: '0.72rem',
                    border: '1px solid #C7D2FE',
                    fontFamily: 'Cairo, Tajawal, sans-serif',
                  }}
                />
              ))}
            </Box>
          )}
        </MotionPaper>

        {/* Grid Card */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.15, ease: 'easeOut' }}
          sx={{
            borderRadius: 3,
            p: { xs: 1.5, sm: 2 },
            bgcolor: '#fff',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
            border: '1px solid #E5E7EB',
          }}
        >
          <DynamicDataGrid
            columns={columns}
            rows={logs}
            loading={status === 'loading'}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            rowCount={count}
            rowsPerPageOptions={[10, 25, 50, 100]}
            paginationProps={{
              labelRowsPerPage: 'عدد الصفوف في الصفحة:',
              labelDisplayedRows: ({ from, to, count }) =>
                `عرض ${from}–${to} من ${count !== -1 ? count : `أكثر من ${to}`}`,
              dir: 'rtl'
            }}
          />
        </MotionPaper>
      </Box>
    </Box>
  );
};

export default LogsPage;